import { useMemo } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { VitalThingsSomnofySm100Session } from "api/generatedApi";
import { SleepStagesChart } from "components/charts";
import {
  convertToSleepData,
  convertToSleepStages,
  convertToSnapData,
} from "components/charts/SleepStagesChart/dataTransform";
import { useFormat } from "hooks/useFormat";

import { useAdvancedStore } from "../advancedStore";
import { ChartCard } from "../components/ChartCard";
import { SeriesToolbar } from "../components/SeriesToolbar";

const stages = ["outOfBed", "awake", "REM", "light", "deep"] as const;
const stageText = {
  outOfBed: "Out of Bed",
  awake: "Awake",
  REM: "REM",
  light: "Light",
  deep: "Deep",
} as const;

const stageNumber = {
  outOfBed: "5",
  awake: "4",
  REM: "3",
  light: "2",
  deep: "1",
} as const;

const dataSeries = [
  {
    id: "respirationRate",
    text: "respiration_rate",
    color: "darkBlue",
    style: "solid",
    gaps: true,
    width: 1,
    dataField: "respiration_rate_mean",
    format: (t: TFunction) => (value: number) =>
      `${value.toFixed(1)} ${t("bpm")}`,
  },
  {
    id: "movement",
    text: "movement",
    color: "turquoise",
    style: "solid",
    width: 1,
    minValue: 100,
    dataField: "movement_fast_mean",
    format: () => (value: number) => value.toFixed(0),
  },
  {
    id: "distance",
    text: "distance",
    color: "mustard",
    style: "solid",
    width: 1,
    dataField: "distance_mean",
    format: (t: TFunction) => (value: number) =>
      `${value.toFixed(2)} ${t("meter_abbreviation_lc")}`,
  },
  {
    id: "light",
    text: "illuminance",
    color: "beige",
    style: "solid",
    width: 1,
    dataField: "light_ambient_mean",
    format: (t: TFunction) => (value: number) =>
      `${value.toFixed(1)} ${t("lux")}`,
  },
  {
    id: "sound",
    text: "Sound amplitude",
    color: "brown",
    style: "solid",
    width: 1,
    dataField: "sound_amplitude_mean",
    format: (t: TFunction) => (value: number) =>
      `${value.toFixed(1)} ${t("dba")}`,
  },
  {
    id: "bioSignal",
    text: "bio_signal_quality",
    color: "orange",
    style: "solid",
    width: 1,
    dataField: "signal_quality_mean",
    format: () => (value: number) => value.toFixed(2),
  },
] as const;

function getTags(session: VitalThingsSomnofySm100Session, t: TFunction) {
  const tags = [
    {
      key: "time_at_intended_sleep",
      datetime: new Date(session.time_at_intended_sleep),
      label: t("Time at Start"),
      offsetIndex: 0,
    },
  ];

  if (session.time_at_sleep_onset) {
    tags.push({
      key: "time_at_sleep_onset",
      datetime: new Date(session.time_at_sleep_onset),
      label: t("First Asleep"),
      offsetIndex: 1,
    });
  }

  if (session.time_at_wakeup) {
    tags.push({
      key: "time_at_wakeup",
      datetime: new Date(session.time_at_wakeup),
      label: t("Woke up"),
      offsetIndex: 0,
    });
  }

  return tags.toSorted((a, b) => b.offsetIndex - a.offsetIndex);
}

type SleepDetailsSectionProps = {
  session: VitalThingsSomnofySm100Session;
};

export function SleepDetailsSection({ session }: SleepDetailsSectionProps) {
  const { t } = useTranslation();
  const { format } = useFormat();
  const theme = useTheme();

  const showAdvanced = useAdvancedStore((state) => state.showAdvanced);
  const setShowAdvanced = useAdvancedStore((state) => state.setShowAdvanced);

  const selections = useAdvancedStore((state) => state.selections);
  const setSelection = useAdvancedStore((state) => state.setSelection);

  const themedStages = useMemo(
    () =>
      stages.map((s) => ({
        key: s,
        sleepStage: stageNumber[s],
        label: t(stageText[s]),
        color: theme.vars.palette.chart.hypnogram[s],
      })),
    [t, theme],
  );

  const availableSeries = useMemo(
    () =>
      dataSeries.filter((d) =>
        session.epoch_data?.[d.dataField]?.some((v) => v > 0),
      ),
    [session.epoch_data],
  );

  const toolbarItems = useMemo(
    () =>
      availableSeries.map((d) => ({
        id: d.id,
        label: t(d.text),
        color: theme.vars.palette.chart.general[d.color],
        checked: selections[d.id] ?? false,
      })),
    [availableSeries, selections, t, theme],
  );

  const minValues = useMemo(
    () =>
      Object.fromEntries(
        dataSeries.map((d) => [d.id, "minValue" in d ? d.minValue : 0]),
      ),
    [],
  );

  const sleepStageData = useMemo(
    () => ({
      tags: getTags(session, t),
      formatXAxisTicks: (d: Date | number) => format(d, "p"),
      formatTooltipDate: (d: Date | number) => format(d, "pp"),
      stageTooltipLabel: t("sleep_stage"),
      stages: themedStages,
      sleepStages: convertToSleepStages(session.epoch_data),
      snapData: convertToSnapData(session.epoch_data),
      lines: showAdvanced
        ? convertToSleepData(
            session.epoch_data,
            availableSeries.map((d) => ({
              ...d,
              gaps: "gaps" in d ? d.gaps : false,
              label: t(d.text),
              formatTooltip: d.format(t),
              color: theme.vars.palette.chart.general[d.color],
            })),
            selections,
            minValues,
          )
        : undefined,
    }),
    [
      session,
      t,
      themedStages,
      format,
      showAdvanced,
      availableSeries,
      selections,
      minValues,
      theme,
    ],
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingInline: 1.5,
          paddingBlock: 0.5,
        }}
      >
        <Typography variant={"bodySmallMedium"}>
          {t("Sleep details")}
        </Typography>
        <FormControlLabel
          control={<Switch />}
          label={t("Advanced data")}
          checked={showAdvanced}
          onChange={(_, value) => setShowAdvanced(value)}
          sx={{ gap: 1, marginRight: 0, displayPrint: "none" }}
        />
      </Box>
      <ChartCard
        title={t("Sleep stages")}
        legend={themedStages}
        toolbar={
          showAdvanced ? (
            <SeriesToolbar items={toolbarItems} onChange={setSelection} />
          ) : undefined
        }
      >
        <SleepStagesChart data={sleepStageData} />
      </ChartCard>
    </Box>
  );
}
